import React, { useEffect, useRef, useState } from "react";

const SLIDES = Array.from(
  { length: 12 },
  (_, index) => `/images/tourists/tour-${String(index + 1).padStart(2, "0")}-soft.webp`
);

export default function TourGallery() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const goTo = (index) => {
    setCurrent((index + SLIDES.length) % SLIDES.length);
  };

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setCurrent((index) => (index + 1) % SLIDES.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;
    const distance = touchStartX.current - event.changedTouches[0].clientX;
    if (Math.abs(distance) > 50) goTo(current + (distance > 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section className="bg-white px-4 pb-8 pt-10 md:px-0 md:py-6" aria-labelledby="tour-gallery-title">
      <div className="relative mx-auto w-full max-w-[900px] my-[50px]">
        <div
          className="group relative aspect-[16/9] w-full overflow-hidden rounded-[24px] bg-white md:rounded-none"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {SLIDES.map((src, index) => (
            <img
              key={src}
              src={src}
              alt={`One Day Korea tour group ${index + 1}`}
              loading={index === 0 ? "eager" : "lazy"}
              fetchpriority={index === 0 ? "high" : "auto"}
              className={[
                "absolute inset-0 h-full w-full object-cover saturate-[.78] contrast-[.96] brightness-[1.02] transition-opacity duration-700",
                index === current ? "opacity-100" : "pointer-events-none opacity-0",
              ].join(" ")}
            />
          ))}

          <button
            type="button"
            onClick={() => goTo(current - 1)}
            className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-2xl text-white opacity-0 backdrop-blur-sm transition hover:bg-black/55 focus:opacity-100 group-hover:opacity-100 md:left-2 md:h-9 md:w-9 md:opacity-100"
            aria-label="Previous photo"
          >
            &#8249;
          </button>
          <button
            type="button"
            onClick={() => goTo(current + 1)}
            className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-2xl text-white opacity-0 backdrop-blur-sm transition hover:bg-black/55 focus:opacity-100 group-hover:opacity-100 md:right-2 md:h-9 md:w-9 md:opacity-100"
            aria-label="Next photo"
          >
            &#8250;
          </button>

          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 md:bottom-3">
            {SLIDES.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goTo(index)}
                className={[
                  "h-2 rounded-full shadow-sm transition-all",
                  index === current ? "w-6 bg-[#E8876A]" : "w-2 bg-white/75 hover:bg-white",
                ].join(" ")}
                aria-label={`Show photo ${index + 1}`}
                aria-current={index === current ? "true" : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
